import { Button } from '@helix-ds/react'
import ProviderAPIClient, {
  SignatureReminderRefusedError,
  TrfSigningUrlError,
  type TrfSigner,
} from '@/features/auth/ProviderAPIClient'
import { getEmailFromSearchParams } from '@/features/auth/emailFromUrl'
import { getLaunchMode } from '@/features/auth/launchContext'
import { docusignSessionEndOutcome } from '@/pages/sign/docusignMessage'
import { signReturnMessageFrom } from '@/pages/sign/signReturnMessage'
import { clearTrfSigningInProgress, markTrfSigningInProgress } from '@/pages/order/useTrfSigningInProgress'
import {
  hasBudgetForAnotherAttempt,
  isRetryableMintStatus,
  SIGN_MINT_RETRY_INTERVAL_MS,
} from '@/pages/sign/signingMintRetry'
import { StatusPill } from '@/components/order/FormCard'
import { trfSigningPath } from '@/features/docusign/signingPath'
import { canSwitchToSigner, rememberSignerAccountSwitch } from '@/features/docusign/signerAccountSwitch'
import { getOrderPatientHeader, type OrderPatientHeaderFields } from '@/utils/DatabaseOperations'
import { formatMrnForDisplay, getPatientInfoFromForm } from '@/utils/orderFormUtils'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { Check, FileLines } from 'flowbite-react-icons/outline'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'

/**
 * Embedded TRF signing surface, at ``/sign/:orderId``.
 *
 * A dedicated, standalone page — not a modal over the order — because the
 * relaxed CSP/COEP needed to frame the DocuSign ceremony is scoped to the
 * ``/sign*`` CloudFront behaviour only; the order page keeps
 * ``Cross-Origin-Embedder-Policy: require-corp`` and could not host the
 * cross-origin iframe.
 *
 * The route sits behind the same session guard as the order pages, so a
 * signed-out clinician arriving from the signing email is sent through
 * ``/login`` and brought back here afterwards. By the time this page renders
 * there is a session (Auth0 or Epic SMART), and a fresh recipient view is minted
 * for the order's TRF at ``POST /orders/{orderId}/trf/signing`` (JWT injected
 * by ProviderAPIClient).
 *
 * The minted recipient-view URL is a single-use capability: it is set straight
 * onto the iframe and is deliberately never logged or rendered as text. The
 * server re-authorizes on every mint (clinician of record, embedded channel,
 * still signable), so this page trusts the endpoint rather than re-deriving
 * those gates.
 *
 * When the ceremony finishes, DocuSign redirects the iframe to our configured
 * return URL (``/sign/return``), which reports the outcome up to this page.
 * That redirect can be blocked, though — a browser refuses to let the signing
 * page navigate the iframe to a private/local return URL (Chrome Private
 * Network Access), and DocuSign can also fail to reach it on a decline. So the
 * page does not depend on it: it also listens for DocuSign's end-of-session
 * message (our SPA origin is in the recipient view's ``messageOrigins``) and
 * shows the Signature Received UI before returning to the order. Either way the
 * manual "View Order" affordance is a guaranteed escape hatch.
 *
 * Patient header: fetched via `getOrderPatientHeader` — never `getOrderById`,
 * which triggers a side-effectful Epic EHR encounter re-sync on every call.
 * Opening the signing page must never trigger encounter state changes as a
 * side effect of painting the patient name.
 *
 * Wrong account: on an Auth0 session that is not the order's clinician of
 * record — typically the same person signed in under another of their
 * addresses — the page signs out and the login page offers the clinician's
 * login, instead of reporting a refusal (see `signerAccountSwitch`). It asks
 * provider-api first (``GET /orders/{orderId}/trf/signer``), which answers as
 * the mint would and names that login. When that cannot answer — including for
 * an account in another organization, refused with a 404 — a refusal from the
 * mint ends the same way, offering the login the link carries. Until a
 * mint has proved the session is the clinician of record, the page renders
 * only a spinner and fetches no patient data.
 */

const SIGNER_LOOKUP_TIMEOUT_MS = 5_000

type ReminderStatus = 'idle' | 'sending' | 'sent' | 'already-sent' | 'error'
type PatientHeaderStatus = 'pending' | 'loaded' | 'failed'

// ── Patient header sub-components ─────────────────────────────────────────────

/**
 * Skeleton placeholder rendered while the patient header is fetching.
 * Fixed minimum height matches the loaded header so nothing below shifts.
 */
function PatientHeaderSkeleton() {
  return (
    <div
      data-testid="patient-header-skeleton"
      className="flex flex-wrap items-center justify-between gap-3 border-b border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4 shadow-sm"
    >
      <div className="flex items-center gap-4">
        <div className="flex flex-col gap-1.5">
          <div className="h-5 w-32 animate-pulse rounded bg-background-quaternary dark:bg-gray-800" />
          <div className="h-3.5 w-20 animate-pulse rounded bg-background-quaternary dark:bg-gray-800" />
        </div>
        <div className="h-10 w-px bg-border-base" />
        <div className="flex flex-col gap-1.5">
          <div className="h-4 w-20 animate-pulse rounded bg-background-quaternary dark:bg-gray-800" />
          <div className="h-3.5 w-24 animate-pulse rounded bg-background-quaternary dark:bg-gray-800" />
        </div>
      </div>
    </div>
  )
}

interface PatientHeaderLoadedProps {
  data: OrderPatientHeaderFields
  onViewOrder: () => void
}

/**
 * Full patient header shown when the order data is available.
 *
 * Name formatting is delegated to `getPatientInfoFromForm` — the single
 * source of truth for the Last, First display convention. We pass the
 * narrowed header fields as a minimal OrderFormInfoData-compatible shape;
 * the helper extracts only the fields it needs and we use only what it
 * returns (name, mrn, age, dob). No other demographics reach this component.
 */
function PatientHeaderLoaded({ data, onViewOrder }: PatientHeaderLoadedProps) {
  // Minimal OrderFormInfoData shape required by getPatientInfoFromForm.
  // Only the patient sub-object is consumed by the helper; the rest of the
  // interface is irrelevant and typed as unknown to avoid needing defaults
  // for fields that getPatientInfoFromForm never reads.
  const minimalFormInfo = {
    patient: {
      id: '',
      name: {
        firstName: data.firstName,
        middleName: data.middleName ?? undefined,
        lastName: data.lastName,
      },
      mrn: data.mrn,
      // sex is not returned by getOrderPatientHeader and is not displayed here.
      sex: undefined,
      // birthDate as string is accepted by BirthDateInput (the format helpers
      // that getPatientInfoFromForm calls accept Date | string | null).
      birthDate: data.birthDate,
    },
  } as unknown as OrderFormInfoData

  const patientInfo = getPatientInfoFromForm(minimalFormInfo)

  const displayName = patientInfo.name.trim() || '—'
  // formatMrnForDisplay strips any existing "MRN" prefix before re-adding "MRN: ",
  // so it is safe against double-prefixing and returns '' for a blank input.
  const displayMrn = formatMrnForDisplay(patientInfo.mrn)
  const displayAge = patientInfo.age.trim()
  const displayDob = patientInfo.dob.trim()

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="flex flex-col gap-0.5">
          <span className="text-base font-semibold leading-6 tracking-tight text-text-heading dark:text-white">
            {displayName}
          </span>
          {displayMrn && (
            <span className="text-xs font-medium leading-5 text-text-body dark:text-gray-400">{displayMrn}</span>
          )}
        </div>
        {(displayAge || displayDob) && (
          <>
            <div className="h-10 w-px shrink-0 bg-border-base" />
            <div className="flex flex-col gap-0.5">
              {displayAge && (
                <span className="text-sm font-bold leading-5 text-text-heading dark:text-white">{displayAge}</span>
              )}
              {displayDob && (
                <span className="text-xs font-medium leading-5 text-text-body dark:text-gray-400">{displayDob}</span>
              )}
            </div>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onViewOrder}
        className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
      >
        <FileLines className="h-4 w-4" aria-hidden />
        <span>View Order</span>
      </button>
    </div>
  )
}

/**
 * Product wordmark — the fallback when the patient header fetch fails. Carries
 * no patient identifier of any kind. Rendered at the same height as the loaded
 * header so nothing below shifts.
 */
function PatientHeaderWordmark() {
  return (
    <div
      data-testid="patient-header-wordmark"
      className="flex items-center border-b border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-4 shadow-sm"
    >
      <span className="text-sm font-semibold tracking-tight text-text-heading dark:text-white">Inflection Medicine</span>
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export function SignTrf() {
  const orderId = useParams<{ orderId: string }>().orderId?.trim() ?? ''
  const [searchParams] = useSearchParams()
  const linkEmail = useMemo(() => getEmailFromSearchParams(searchParams), [searchParams])

  const [signingUrl, setSigningUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [signatureReceived, setSignatureReceived] = useState<boolean>(false)
  const [reminderStatus, setReminderStatus] = useState<ReminderStatus>('idle')

  const [patientHeaderStatus, setPatientHeaderStatus] = useState<PatientHeaderStatus>('pending')
  const [patientHeader, setPatientHeader] = useState<OrderPatientHeaderFields | null>(null)

  // Tracks whether an active signing iframe is present, so a stray session-end
  // message received when no iframe exists (e.g. after a mint error) does not
  // incorrectly flip into the Signature Received state.
  const hasSigningUrlRef = useRef(false)

  // Identifies the live mint attempt. Bumped to start one, which abandons the
  // previous: a loop whose generation is stale resolves without touching state,
  // so a retry the clinician asked for cannot be overwritten by the run it
  // replaced. Deliberately not bumped on unmount — ``decidedRef`` below holds
  // across StrictMode's mount→unmount→remount and would leave the abandoned
  // attempt with nothing to restart it.
  const mintGenerationRef = useRef(0)
  // Guards against a non-completion terminal message received after the
  // Signature Received state is set from un-setting it.
  const signatureReceivedRef = useRef(false)

  const client = useMemo(() => ProviderAPIClient.getInstance(), [])

  // Inside Hyperspace there is no dashboard to offer: /orders sits behind the
  // Auth0-only guard, which an Epic SMART session does not satisfy.
  const hasEpicAuth = getLaunchMode() === 'epic_smart'

  const { user: auth0User, logout: auth0Logout } = useAuth0()
  const sessionEmail = auth0User?.email?.trim() ?? ''
  const signerRef = useRef<TrfSigner | null>(null)

  // Nothing about the order — not even the patient's name — is shown until
  // provider-api has answered a mint in a way only the clinician of record can
  // be answered: a URL, or a 409, which it raises only past its authorization.
  const [accessGranted, setAccessGranted] = useState(false)

  // Null on failure or a slow answer: the lookup only decides whether to offer
  // a different login, the mint's own authorization still stands behind it,
  // and it must never be what holds the ceremony up.
  const lookUpSigner = useCallback(
    async (targetOrderId: string) => {
      if (signerRef.current) return signerRef.current
      const giveUp = new Promise<null>((resolve) => setTimeout(() => resolve(null), SIGNER_LOOKUP_TIMEOUT_MS))
      signerRef.current = await Promise.race([client.getTrfSigner(targetOrderId).catch(() => null), giveUp])
      return signerRef.current
    },
    [client]
  )

  // The login to send this session to, or null to leave the mint to decide.
  // Without an answer from provider-api, the address the link was mailed to is
  // the best guess; with one that names no login, nobody else could sign either.
  const switchEmailFor = useCallback(
    (signer: TrfSigner | null): string | null => {
      const email = signer === null ? linkEmail : signer.isSigner ? null : signer.signerEmail
      return canSwitchToSigner(email, sessionEmail) ? email : null
    },
    [linkEmail, sessionEmail]
  )

  // An Epic SMART session has no Auth0 login to swap, so it never gets here.
  const switchToSigner = useCallback(
    (targetOrderId: string, email: string) => {
      setError(null)
      setLoading(false)
      rememberSignerAccountSwitch({ email, returnTo: trfSigningPath(targetOrderId) })
      void auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
    },
    [auth0Logout]
  )

  // Full document load (not a client-side route change) back to the order, so it
  // is served under its own CloudFront behaviour/headers rather than inheriting
  // the /sign* relaxed CSP. Falls back to the orders list when the order id is
  // somehow absent.
  const returnToOrder = useCallback(() => {
    window.location.assign(orderId ? `/order/in-progress/${encodeURIComponent(orderId)}` : '/orders')
  }, [orderId])

  // The order page's "checking your signature status" cue reads an intent from
  // this tab's sessionStorage. Recording it here, where the outcome is known,
  // covers a ceremony entered from the emailed link as well as from Sign TRF, and
  // keeps an unsigned ending from showing the cue at all.
  const endCeremony = useCallback(
    (signed: boolean) => {
      if (signed) {
        signatureReceivedRef.current = true
        markTrfSigningInProgress(orderId)
        setSignatureReceived(true)
      } else {
        clearTrfSigningInProgress(orderId)
        returnToOrder()
      }
    },
    [orderId, returnToOrder]
  )

  // ── Auto-redirect to the order ──────────────────────────────────────────────
  // Fires 5 seconds after the Signature Received state appears. Any pointer
  // interaction on the page cancels it. The order is where the signature is
  // about to appear, so it is the destination on every path — including Epic
  // SMART, which stays inside the EHR workspace.
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Pointer interaction clears the timer without a re-render unless this flips,
  // which would leave "Taking you back to the order" up after navigation has
  // been cancelled.
  const [redirectCancelled, setRedirectCancelled] = useState(false)

  const cancelRedirect = useCallback(() => {
    if (redirectTimerRef.current !== null) {
      clearTimeout(redirectTimerRef.current)
      redirectTimerRef.current = null
    }
    setRedirectCancelled(true)
  }, [])

  useEffect(() => {
    if (!signatureReceived) return
    setRedirectCancelled(false)
    redirectTimerRef.current = setTimeout(() => {
      returnToOrder()
    }, 5000)
    // Cancel on any pointer interaction anywhere in the document so a button
    // click (e.g. View Order) is sufficient to stop the redirect. Must be a
    // document-level listener so it catches events that originate outside the
    // component's subtree.
    document.addEventListener('pointerdown', cancelRedirect, { once: true })
    return () => {
      if (redirectTimerRef.current !== null) {
        clearTimeout(redirectTimerRef.current)
        redirectTimerRef.current = null
      }
      document.removeEventListener('pointerdown', cancelRedirect)
    }
  }, [signatureReceived, cancelRedirect, returnToOrder])

  // ── DocuSign session-end message ─────────────────────────────────────────────
  // Completion channel that does not rely on the post-sign returnUrl redirect. DocuSign posts a terminal session-end message when the
  // ceremony ends. The outcome governs what happens next:
  //   'complete'  → show Signature Received UI (a real signature was captured).
  //   'incomplete' → return to the order via location.assign (the blocked-redirect
  //                  escape — the signer cancelled/declined/timed out).
  // Guarded so that: a stray message with no active iframe does not flip into
  // success (hasSigningUrlRef), and a non-completion event received after the
  // Signature Received state is set does not un-set it (signatureReceivedRef).
  //
  // Our own return frame reports the same ending, from the redirect DocuSign
  // performs rather than the message it posts. Either signal is enough and the
  // second is ignored, which is the point: the redirect is configured by us and
  // always happens, while the message depends on DocuSign's `messageOrigins`
  // and on DocuSign choosing to send it.
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (!hasSigningUrlRef.current || signatureReceivedRef.current) return

      const fromReturnFrame = signReturnMessageFrom(e.origin, e.data)
      if (fromReturnFrame !== null) {
        // Nothing was signed on 'cancelled': back to the order to try again.
        // 'unknown' counts as an ending. DocuSign returned without naming an
        // outcome, the ceremony is over either way, and the copy does not claim
        // a signature was captured — the order's own status remains the source
        // of truth.
        endCeremony(fromReturnFrame.outcome !== 'cancelled')
        return
      }

      const outcome = docusignSessionEndOutcome(e.origin, e.data)
      if (outcome === null) return
      endCeremony(outcome === 'complete')
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [endCeremony])

  // ── Patient header fetch ────────────────────────────────────────────────────
  // Uses getOrderPatientHeader — never getOrderById — so that rendering the
  // patient name does not trigger syncEpicEhrEncounterFromOrderGet as a side
  // effect. The catch path captures only an opaque error kind; no field values
  // or patient data are included so that Sentry and breadcrumbs never carry
  // identifiers from this page (it runs under a relaxed CSP). Not requested
  // until access is granted, so another account never holds the name at all.
  const headerFetchedRef = useRef(false)
  useEffect(() => {
    if (!orderId || !accessGranted || headerFetchedRef.current) return
    headerFetchedRef.current = true
    void getOrderPatientHeader(orderId, 'sign.header')
      .then((data) => {
        setPatientHeaderStatus('loaded')
        setPatientHeader(data)
      })
      .catch(() => {
        Sentry.captureException(new Error('patient-header-fetch-failed'), {
          tags: { operation: 'sign.header.fetch' },
        })
        setPatientHeaderStatus('failed')
      })
  }, [orderId, accessGranted])

  /**
   * Ask for a signing URL, holding the preparing state while the answer can
   * still change.
   *
   * The envelope is built after the order commits, so a clinician arriving
   * straight from submit routinely asks before there is anything to sign and is
   * refused for a few seconds. Treating that first refusal as final is what sent
   * them to an error page for a signing session that was about to exist, so the
   * refusals that resolve on their own are waited out instead — up to the minute
   * the preparing card promises. A refusal that will never change is surfaced at
   * once; waiting on it would only delay the truth.
   */
  const runMintWithRetry = useCallback(
    async (targetOrderId: string) => {
      const generation = ++mintGenerationRef.current
      const superseded = () => mintGenerationRef.current !== generation
      const startedAt = Date.now()

      setError(null)
      setLoading(true)

      for (;;) {
        try {
          const { url } = await client.createTrfSigningUrl(targetOrderId)
          if (superseded()) return
          setAccessGranted(true)
          hasSigningUrlRef.current = true
          setSigningUrl(url)
          setLoading(false)
          return
        } catch (e) {
          if (superseded()) return
          if (e instanceof TrfSigningUrlError && e.status === 409) setAccessGranted(true)
          // A 404 is the same refusal for an account in another organization,
          // which provider-api does not tell that the order exists.
          const refused = e instanceof TrfSigningUrlError && (e.status === 403 || e.status === 404)
          if (refused && !hasEpicAuth) {
            const email = switchEmailFor(await lookUpSigner(targetOrderId))
            if (superseded()) return
            // Expected, not reported: the person behind the session is the
            // clinician, signed in as a different account.
            if (email) {
              switchToSigner(targetOrderId, email)
              return
            }
          }
          // Only a refusal the client classified is worth repeating. Anything
          // else reaching here is unexpected, and retrying it would bury the
          // cause under a minute of spinner.
          const retryable = e instanceof TrfSigningUrlError && isRetryableMintStatus(e.status)
          if (!retryable || !hasBudgetForAnotherAttempt(Date.now() - startedAt)) {
            // Reported once the attempt is abandoned, so a preparation window
            // that resolved normally does not register as a failure.
            Sentry.captureException(e, { tags: { operation: 'sign.trf.mint-signing-url' } })
            setError('We could not start the signing session. Please return to the order and try again.')
            setLoading(false)
            return
          }
          await new Promise((resolve) => setTimeout(resolve, SIGN_MINT_RETRY_INTERVAL_MS))
          if (superseded()) return
        }
      }
    },
    [client, hasEpicAuth, lookUpSigner, switchEmailFor, switchToSigner]
  )

  // ── Signing URL mint ─────────────────────────────────────────────────────────
  // A single-use URL must be minted exactly once per page load. The ref persists
  // across React 18 StrictMode's mount→unmount→remount, so the mint fires once
  // and its state updates are never cancelled by the interim cleanup.
  const decidedRef = useRef(false)

  useEffect(() => {
    if (decidedRef.current) return
    decidedRef.current = true

    if (!orderId) {
      setError('Missing order reference. Return to the order and try again.')
      setLoading(false)
      return
    }

    const start = async () => {
      if (!hasEpicAuth) {
        // Unanswered, the guess from the link waits for a 403 to confirm it.
        const signer = await lookUpSigner(orderId)
        const email = signer ? switchEmailFor(signer) : null
        if (email) {
          switchToSigner(orderId, email)
          return
        }
      }
      await Sentry.startSpan({ op: 'http.client', name: 'POST /orders/{orderId}/trf/signing' }, () =>
        runMintWithRetry(orderId)
      )
    }
    void start()
  }, [orderId, hasEpicAuth, lookUpSigner, switchEmailFor, switchToSigner, runMintWithRetry])

  // ── Reminder email ────────────────────────────────────────────────────────────
  // One request, no waiting on this side. The clinician usually clicks before
  // there is an envelope to link to — the button sits on the preparing card
  // precisely so they need not wait one out — and provider-api now records that
  // ask and discharges it when the envelope arrives. So a refusal here is a
  // final one: already sent, or an order that cannot be signed in-app at all.
  const handleSendReminder = useCallback(async () => {
    if (!orderId || reminderStatus === 'sending') return
    setReminderStatus('sending')

    try {
      await client.sendTrfSignatureReminder(orderId)
      setReminderStatus('sent')
    } catch (e) {
      if (e instanceof SignatureReminderRefusedError && e.refusal === 'already-sent') {
        setReminderStatus('already-sent')
        return
      }
      Sentry.captureException(e, { tags: { operation: 'sign.trf.send-reminder' } })
      setReminderStatus('error')
    }
  }, [orderId, reminderStatus, client])

  // The button renders only inside the preparing card, so the mint is always in
  // flight while it is on screen: gating on `loading` or `signingUrl` would leave
  // it disabled for its entire visible life, and by the time either clears the
  // card has been replaced by the embed. Sending the reminder needs `orderId`,
  // which comes from the path, not the mint.
  //
  // 'sent' and 'already-sent' are terminal: the per-envelope reminder claim is
  // consumed and no second email is possible. 'error' stays retryable.
  const emailButtonDisabled =
    !orderId ||
    reminderStatus === 'sending' ||
    reminderStatus === 'sent' ||
    reminderStatus === 'already-sent'

  // ── Patient header rendering ───────────────────────────────────────────────
  // Pending: skeleton at fixed height.
  // Failed/null: wordmark — no identifier of any kind.
  // Loaded: full header with name, MRN, age, DOB, View Order.
  // Never granted (a refusal surfaced as an error): wordmark.
  const renderPatientHeader = () => {
    if (!accessGranted) {
      return <PatientHeaderWordmark />
    }
    if (patientHeaderStatus === 'pending') {
      return <PatientHeaderSkeleton />
    }
    if (patientHeaderStatus === 'failed' || patientHeader === null) {
      return <PatientHeaderWordmark />
    }
    return <PatientHeaderLoaded data={patientHeader} onViewOrder={returnToOrder} />
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  // While access is unproven — including on the way out to log in as another
  // account — nothing of the signing page is drawn, so it cannot look as if the
  // form is about to open for someone who will never be shown it.
  if (!accessGranted && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div
          role="status"
          data-testid="sign-access-check"
          className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-brand-600 dark:border-brand-500 border-r-transparent"
        >
          <span className="sr-only">Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-5xl flex-col">
      {/* ── Patient header ────────────────────────────────────────────── */}
      {renderPatientHeader()}

      {/* ── "Sign Requisition Form" meta-card ───────────────────────── */}
      {/* Always visible regardless of the signing state. */}
      <div className="mx-6 mt-4 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-4 shadow-card">
        <h1 className="text-2xl font-semibold leading-8 tracking-tight text-text-heading dark:text-white">
          Sign Requisition Form
        </h1>
        <div className="mt-2">
          <StatusPill text="Review the form and sign below." variant="info" />
        </div>
      </div>

      {/* ── Content area ──────────────────────────────────────────────── */}
      <div className="mx-6 mb-6 mt-4 flex flex-1 flex-col">
        {/* Signature Received state */}
        {signatureReceived && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 shadow-card">
            {/* Green circular check icon */}
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-50 dark:bg-gray-900">
              <Check className="h-6 w-6 text-success-600" aria-hidden="true" />
            </div>

            <div className="flex flex-col items-center gap-1 text-center">
              <p className="text-xl font-semibold tracking-tight text-text-heading dark:text-white">
                Signature Received
              </p>
              <p className="text-sm font-medium text-text-body-subtle dark:text-gray-500">
                We&apos;re recording it on the order.
              </p>
            </div>

            {/* Primary: View Order (filled) — it matches where the timer is
                already taking them. Dashboard is the secondary escape, and is
                omitted on Epic SMART: /orders is behind the Auth0-only guard,
                so a clinician this page treats as signed in via Hyperspace
                would bounce to /login and out of the EHR workspace. */}
            <div className="flex flex-col items-center gap-3 sm:flex-row">
              <Button
                type="button"
                variant="primary"
                size="md"
                onClick={returnToOrder}
                className="focus-visible:ring-brand-600"
              >
                View Order
              </Button>
              {!hasEpicAuth && (
                <button
                  type="button"
                  onClick={() => window.location.assign('/orders')}
                  className="inline-flex items-center justify-center rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-6 py-2.5 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800"
                >
                  Go to Dashboard
                </button>
              )}
            </div>

            {/* Auto-redirect notice — only while the redirect is still armed. */}
            {!redirectCancelled && (
              <p className="text-xs text-text-body-subtle dark:text-gray-500">
                Taking you back to the order&hellip;
              </p>
            )}
          </div>
        )}

        {/* Preparing / embed / error states */}
        {!signatureReceived && (
          <>
            {/* ── Preparing state ─────────────────────────────────────── */}
            {loading && (
              <div
                role="status"
                aria-live="polite"
                className="flex flex-1 flex-col items-center justify-center gap-3 rounded-base border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center shadow-card"
              >
                {/* helix-exception: spinner kept; Flowbite Spinner would add role="status" and a label (F-13) */}
                <svg
                  className="h-6 w-6 animate-spin text-brand-600 dark:text-brand-400"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                  />
                </svg>
                <span className="text-xl font-semibold leading-6 tracking-tight text-text-heading dark:text-white">
                  Preparing the requisition form for your signature...
                </span>
                <span className="text-sm font-medium leading-5 text-text-body-subtle dark:text-gray-500">
                  This usually takes under a minute.
                </span>

                {/* Email button and footnote inside the preparing card. */}
                <button
                  type="button"
                  disabled={emailButtonDisabled}
                  onClick={() => void handleSendReminder()}
                  className="inline-flex w-fit items-center justify-center rounded-full border border-border-base dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-body dark:text-gray-400 shadow-xs transition-colors hover:bg-background-secondary-medium dark:hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Email me a signing link instead
                </button>

                {reminderStatus === 'sending' && (
                  <p className="text-sm font-medium leading-5 text-text-body-subtle dark:text-gray-500">
                    Sending your signing link&hellip;
                  </p>
                )}
                {/* "On its way" rather than "sent": the request is accepted
                    before the email leaves, and when the envelope is still
                    being prepared it leaves a few seconds later. */}
                {reminderStatus === 'sent' && (
                  <p className="text-sm font-medium text-text-fg-success dark:text-success-100">
                    A signing link is on its way to your email.
                  </p>
                )}
                {reminderStatus === 'already-sent' && (
                  <p className="text-sm font-medium leading-5 text-text-body-subtle dark:text-gray-500">
                    A signing link was already sent to your email.
                  </p>
                )}
                {reminderStatus === 'error' && (
                  <p role="alert" className="text-sm font-medium text-text-fg-error dark:text-error-100">
                    We could not send the signing link. Please try again or return to the order.
                  </p>
                )}

                <p className="max-w-[452px] text-sm font-medium leading-5 text-text-body dark:text-gray-400">
                  You can sign anytime from the order page. If the form is still unsigned after
                  10 minutes, we&apos;ll email you a link.
                </p>
              </div>
            )}

            {error && (
              <div
                role="alert"
                className="flex flex-col items-start gap-3 rounded-base border border-border-error-subtle dark:border-error-700 bg-error-50 dark:bg-gray-900 px-4 py-3 text-sm font-medium text-text-fg-error dark:text-error-100"
              >
                <span>{error}</span>
                {orderId && (
                  <button
                    type="button"
                    onClick={() => void runMintWithRetry(orderId)}
                    className="inline-flex w-fit items-center justify-center rounded-full border border-border-error-subtle dark:border-error-700 bg-white dark:bg-gray-900 px-4 py-2 text-sm font-medium text-text-fg-error dark:text-error-100 shadow-xs transition-colors hover:bg-error-50 dark:hover:bg-gray-800"
                  >
                    Try again
                  </button>
                )}
              </div>
            )}

            {/* ── Embed state ──────────────────────────────────────────── */}
            {/* Dark filled container. Email and footnote block are hidden while
                the iframe is mounted (per design). */}
            {signingUrl && (
              <div className="overflow-hidden rounded-base bg-background-dark">
                <iframe
                  title="DocuSign signing ceremony"
                  src={signingUrl}
                  className="h-[80vh] w-full flex-1"
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default SignTrf
